import type { DatePickerMode } from '@shared/types/date';

import dayjs, { type ConfigType } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import weekOfYear from 'dayjs/plugin/weekOfYear';

dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);
dayjs.extend(quarterOfYear);

function normalizeTime(value: ConfigType, format?: string | null) {
    const date = dayjs(value);
    const normalized = dayjs()
        .startOf('day')
        .hour(date.hour())
        .minute(date.minute());

    return format?.includes('ss')
        ? normalized.second(date.second())
        : normalized.second(0);
}

function normalizeWeek(value: ConfigType) {
    const date = dayjs(value);
    return date.startOf('week');
}

function normalizeByDateType(
    value: ConfigType,
    dateType: DatePickerMode | null | undefined,
    format?: string | null,
) {
    const date = dayjs(value);

    switch (dateType) {
        case 'year':
            return date.startOf('year');
        case 'quarter':
            return date.startOf('quarter');
        case 'month':
            return date.startOf('month');
        case 'week':
            return normalizeWeek(value);
        case 'time':
            return normalizeTime(value, format);
        case 'datetime':
            return date.startOf('minute');
        case 'date':
        default:
            return date.startOf('day');
    }
}

export function isAfterCurrentPeriod(
    value: ConfigType,
    dateType: DatePickerMode | null | undefined,
    format?: string | null,
): boolean {
    return normalizeByDateType(value, dateType, format).isAfter(
        normalizeByDateType(dayjs(), dateType, format),
    );
}

export function isSameOrAfterCurrentPeriod(
    value: ConfigType,
    dateType: DatePickerMode | null | undefined,
    format?: string | null,
): boolean {
    const current = normalizeByDateType(dayjs(), dateType, format);
    const target = normalizeByDateType(value, dateType, format);

    return target.isSame(current) || target.isAfter(current);
}

export function isBeforeCurrentPeriod(
    value: ConfigType,
    dateType: DatePickerMode | null | undefined,
    format?: string | null,
): boolean {
    return normalizeByDateType(value, dateType, format).isBefore(
        normalizeByDateType(dayjs(), dateType, format),
    );
}

export function isSameOrBeforeCurrentPeriod(
    value: ConfigType,
    dateType: DatePickerMode | null | undefined,
    format?: string | null,
): boolean {
    const current = normalizeByDateType(dayjs(), dateType, format);
    const target = normalizeByDateType(value, dateType, format);

    return target.isSame(current) || target.isBefore(current);
}
